import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, timeout
  const pollingRef = useRef({ count: 0, active: true });

  useEffect(() => {
    // Poll /auth/me up to ~30s, looking for is_pro = true
    const maxAttempts = 15;
    const pollInterval = 2000;

    const poll = async () => {
      if (!pollingRef.current.active) return;
      if (pollingRef.current.count >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      pollingRef.current.count++;

      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (data.is_pro) {
          pollingRef.current.active = false;
          await refreshUser();
          setStatus("success");
          return;
        }
        setTimeout(poll, pollInterval);
      } catch {
        setTimeout(poll, pollInterval);
      }
    };

    poll();

    return () => {
      pollingRef.current.active = false;
    };
  }, [refreshUser]);

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden text-white">
      <div
        className="fixed inset-0 z-[-1]"
        style={{
          backgroundImage: `url('/bg-hero.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8 text-center">
        {status === "checking" && (
          <>
            <div className="w-20 h-20 border-4 border-[#00F0FF] border-t-transparent rounded-full animate-spin mb-6" />
            <h1
              className="text-4xl sm:text-5xl font-primary text-white mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}
              data-testid="payment-checking-title"
            >
              Activating your Pro account...
            </h1>
            <p className="text-[#A1A1AA]">This usually takes a few seconds.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-24 h-24 rounded-full bg-[#FFEA00]/20 border-2 border-[#FFEA00] flex items-center justify-center mb-6 animate-pulse">
              <svg className="w-12 h-12 text-[#FFEA00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1
              className="text-5xl sm:text-7xl font-primary text-[#FFEA00] drop-shadow-[0_0_20px_rgba(255,234,0,0.6)] mb-2"
              style={{ fontFamily: "Anton, sans-serif" }}
              data-testid="payment-success-title"
            >
              YOU'RE PRO!
            </h1>
            <p className="text-[#A1A1AA] text-lg mb-8">
              Thank you for upgrading. Time to host some games!
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-8 py-4 bg-[#00F0FF] text-black font-primary text-2xl rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.5)] hover:scale-[1.02] active:scale-95 transition-all uppercase"
              style={{ fontFamily: "Anton, sans-serif" }}
              data-testid="payment-success-continue"
            >
              Start Hosting
            </button>
          </>
        )}

        {status === "timeout" && (
          <>
            <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center mb-6">
              <svg className="w-10 h-10 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1
              className="text-3xl sm:text-4xl font-primary text-white mb-4"
              style={{ fontFamily: "Anton, sans-serif" }}
              data-testid="payment-timeout-title"
            >
              Payment is processing
            </h1>
            <p className="text-[#A1A1AA] mb-8 max-w-md">
              Your purchase is being confirmed. This can take up to a minute. Refresh this page or check your account in a moment — your Pro access will appear automatically once payment is verified.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-[#FFEA00] text-black font-primary rounded-xl hover:scale-[1.02] active:scale-95 transition-all uppercase"
                style={{ fontFamily: "Anton, sans-serif" }}
                data-testid="payment-refresh-button"
              >
                Refresh
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-3 bg-white/10 text-white border border-white/20 rounded-xl hover:bg-white/20 transition-all"
                data-testid="payment-home-button"
              >
                Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
